/**
 * Home-feed card: car news digest.
 *
 * Pulls the most-recent vehicle recalls from the CKAN dataset (same one
 * `vehicleAnomalyChecks` uses on the server) and shows the 3 newest as a
 * "what's happening in cars" feed. Future iterations can add RSS sources.
 *
 * Why CKAN recalls and not real news: data.gov.il is the only Hebrew car
 * data source we can hit directly from the browser without CORS. Real
 * RSS feeds from car magazines need a server-side proxy — out of scope
 * for this first card iteration.
 */

import * as ckan from '../../data/apis/ckan.js';
import { createLogger } from '../../core/logger.js';
import { escape as esc } from '../../shared/escape.js';

const log = createLogger('news-card');

const RECALL_RESOURCE = '2c33523f-87aa-44ec-a736-edbb0a82975e';
const CACHE_KEY = 'c2c_news_cache';
const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6h

/**
 * Render the news card. The host is provided by homeView with a `b-mini-card`
 * base — we attach the D4 skin and inject items as a styled list. The card
 * uses the live-dot to signal "fresh content streaming".
 */
export async function renderInto(host) {
  if (!host) return;
  host.classList.add('b-mini-card', 'cat-news', 'b-row-full');
  host.innerHTML = `
    <div class="b-top-row"><div class="b-badge">📰</div><span class="b-live-dot"></span></div>
    <div>
      <div class="b-title">חדשות יצרני הרכב</div>
      <div class="b-news-list" style="margin-top:6px;font-size:13px;color:var(--ink-700);line-height:1.6;"><div class="b-sub">⏳ טוען...</div></div>
    </div>
  `;
  try {
    const items = await getNewsItems();
    const list = host.querySelector('.b-news-list');
    if (!list) return;
    if (items.length === 0) {
      list.innerHTML = `<div class="b-sub">אין חדשות חדשות.</div>`;
      return;
    }
    list.innerHTML = items.slice(0, 3).map((it) => `
      <a href="${esc(it.url || 'https://www.gov.il/he/departments/topics/recall-vehicle-list')}"
         target="_blank" rel="noopener"
         style="display:block;padding:6px 0;color:var(--ink-700);text-decoration:none;border-bottom:1px solid var(--line);">
        <div style="font-weight:600;color:var(--ink-900);font-size:13px;">▸ ${esc(it.title)}</div>
        <div style="font-size:11px;color:var(--ink-500);margin-top:2px;">${esc(it.source)}</div>
      </a>
    `).join('');
  } catch (err) {
    log.warn('news card failed:', err?.message);
    const list = host.querySelector('.b-news-list');
    if (list) list.innerHTML = `<div class="b-sub">לא ניתן לטעון חדשות כעת.</div>`;
  }
}

// ====================================================================

async function getNewsItems() {
  // Cache to avoid daily CKAN hits.
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const c = JSON.parse(raw);
      if (c && c.at && Date.now() - c.at < CACHE_TTL_MS) return c.items;
    }
  } catch (_) { /* ignore */ }

  // Recent recalls = highest _id = most recently published row.
  // Use `ckan.search()` so CORS is handled via the ckanProxy Cloud Function.
  const records = await ckan.search({
    resourceId: RECALL_RESOURCE,
    limit: 10,
    sort: '_id desc',
  });

  const items = records.slice(0, 5).map((r) => ({
    title: `${String(r.TOZAR_TEUR || '').trim()} ${String(r.DEGEM || '').trim()} — ${String(r.TEUR_TAKALA || r.SUG_TAKALA || 'קריאה').trim()}`.slice(0, 110),
    source: `🔧 קריאת יצרן · ${String(r.SHNAT_RECALL || '')}`,
    url: String(r.WEBSITE || '').trim() || null,
  }));

  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), items })); }
  catch (_) { /* ignore */ }
  return items;
}
